// src/subscreens/mechanicadmin/disputes/MechanicDisputes.jsx

import React, { useState, useRef, useEffect, useContext } from "react";
import {
  FiAlertTriangle, FiCheckCircle, FiClock,
  FiTrash2, FiEye, FiX, FiTool, FiMoreVertical, FiSend
} from "react-icons/fi";
import MechanicAdminLayout from "../../../layout/mechanic/MechanicMasterLayout";
import "../../../assets/css/mechanic/dispute/Dispute.css";
import AppContext from "../../../context/AppContext";
import axios from "axios"; 
import Swal from "sweetalert2";

export default function MechanicDisputes() {
  const { baseUrl, loggedInMechanic, baseUrlForFile , authToken} = useContext(AppContext);

  const [disputes,       setDisputes]       = useState([]);
  const [selectedDispute,setSelectedDispute]= useState(null);
  const [showModal,      setShowModal]      = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [chatMessages,   setChatMessages]   = useState([]);
  const [message,        setMessage]        = useState("");
  const [activeMenu,     setActiveMenu]     = useState(null);
  const [forwarding,     setForwarding]     = useState(false);

  const messagesEndRef = useRef(null);

  // ── Stats ──
  const total      = disputes.length;
  const open       = disputes.filter(d => d.status === "pending").length;
  const underReview= disputes.filter(d => d.status === "active").length;
  const resolved   = disputes.filter(d => d.status === "resolved").length;

  // ── Fetch disputes ──
  // NOTE: route prefix is "mechanicadmin" (not "mechanic") per routes/api.php
  const viewMyDisputes = () => {
    axios.post(`${baseUrl}/mechanicadmin/dispute/viewMyDisputes`, {
      srmechanicadmin_id: loggedInMechanic.id,
    }, {
      headers: { Authorization: `Bearer ${authToken}` }
    }
  ).then((res) => {
      if (res.data.success) setDisputes(res.data.request || []);
    }).catch((err) => console.log(err));
  };

  useEffect(() => {
    if (loggedInMechanic?.id) viewMyDisputes();
  }, [loggedInMechanic]);

  // ── Scroll to bottom ──
  useEffect(() => {
    if (showModal && messagesEndRef.current)
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, showModal]);

  // ── Close dropdown on outside click ──
  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest(".ds-action-menu")) setActiveMenu(null);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  // ── Fetch chat ──
  const fetchChatForDispute = (dispute) => {
    axios.post(`${baseUrl}/mechanicadmin/dispute/fetchChatForDispute`, {
      srdispute_id: dispute.id,
    } , {
      headers: { Authorization: `Bearer ${authToken}` }
    }
  ).then((res) => {
      if (res.data.success) {
        setConversationId(res.data.srconversation_id);
        setChatMessages(res.data.chats || []);
      } else {
        setConversationId(null);
        setChatMessages([]);
      }
    }).catch((err) => console.log(err));
  };

  // ── Open modal ──
  const openDispute = (dispute) => {
    setSelectedDispute(dispute);
    setShowModal(true);
    setMessage("");
    setActiveMenu(null);
    fetchChatForDispute(dispute);
  };

  // ── Close modal ──
  const closeModal = () => {
    setShowModal(false);
    setSelectedDispute(null);
    setMessage("");
    setChatMessages([]);
    setConversationId(null);
  };

  // ── Send message ──
  const sendMessage = () => {
    if (!message.trim() || !conversationId) return;
    axios.post(`${baseUrl}/mechanicadmin/dispute/sendMessageForDispute`, {
      srconversation_id: conversationId,
      message: message,
    } , {
      headers: { Authorization: `Bearer ${authToken}` }
    }
  ).then((res) => {
      if (res.data.success) {
        setMessage("");
        fetchChatForDispute(selectedDispute);
      }
    }).catch((err) => console.log(err));
  };

  // ── Mark as resolved ──
  // NOTE: there is no "markAsResolved" route registered — the actual
  // route in routes/api.php is "resolveDispute", so this now calls that.
  const markAsResolved = () => {
    if (!selectedDispute) return;
    axios.post(`${baseUrl}/mechanicadmin/dispute/resolveDispute`, {
      srdispute_id: selectedDispute.id,
    } , {
      headers: { Authorization: `Bearer ${authToken}` }
    }).then((res) => {
      if (res.data.success) {
        setDisputes(disputes.map(d =>
          d.id === selectedDispute.id ? { ...d, status: "resolved" } : d
        ));
        closeModal();
      }
    }).catch((err) => console.log(err));
  };

  // ── Delete ──
  const deleteDispute = (id) => {
    setActiveMenu(null);
    if (window.confirm("Are you sure you want to delete this dispute?"))
      setDisputes(disputes.filter(d => d.id !== id));
  };

  // ── Forward dispute to superadmin ──
  const handleForwardToSuperadmin = (dispute) => {
    setActiveMenu(null);

    Swal.fire({
      title: "Are you sure?",
      text: "This dispute will be forwarded to the Superadmin for resolution.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, forward it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#FDB714",
    }).then((result) => {
      if (!result.isConfirmed) return;

      setForwarding(true);

      axios.post(`${baseUrl}/mechanicadmin/dispute/forwardToSuperadmin`, {
        srdispute_id: dispute.id,
      }, {
        headers: { Authorization: `Bearer ${authToken}` }
      }).then((res) => {
        setForwarding(false);
        if (res.data.success) {
          setDisputes((prev) =>
            prev.map((d) =>
              d.id === dispute.id ? { ...d, forwarded: true } : d
            )
          );
          Swal.fire({
            title: "Forwarded!",
            text: "The dispute has been forwarded to the Superadmin.",
            icon: "success",
            confirmButtonColor: "#FDB714",
          });
        } else {
          Swal.fire({
            title: "Failed",
            text: res.data.message || "Could not forward this dispute.",
            icon: "error",
            confirmButtonColor: "#FDB714",
          });
        }
      }).catch((err) => {
        setForwarding(false);
        console.log(err);
        Swal.fire({
          title: "Error",
          text: "Something went wrong while forwarding this dispute.",
          icon: "error",
          confirmButtonColor: "#FDB714",
        });
      });
    });
  };

  // ── Helpers ──
  const getStatusBadge = (status) => {
    if (status === "pending")  return <span className="ds-badge ds-badge-open">Open</span>;
    if (status === "active")   return <span className="ds-badge ds-badge-review">Under Review</span>;
    if (status === "resolved") return <span className="ds-badge ds-badge-resolved">Resolved</span>;
    return <span className="ds-badge">{status}</span>;
  };

  const getForwardedBadge = (isForwarded) =>
    isForwarded
      ? <span className="ds-badge ds-badge-resolved">Yes</span>
      : <span className="ds-badge">No</span>;

  const formatTime = (datetime) => {
    if (!datetime) return "";
    return new Date(datetime).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  const getSenderLabel = (sender) => {
    if (sender === "mechanicadmin") return;
    if (sender === "customer")      return "Customer";
    if (sender === "rentaladmin")   return "Rental Admin";
    if (sender === "superadmin")    return "Superadmin";
    return sender;
  };

  // ── Resolve "Raised By" from the dispute relations ──
  // A dispute is raised either by the customer (srcustomer) or the
  // rental admin (srrentaladmin) — whichever relation is populated.
  // NOTE: confirm the actual column names on your Srcustomer / Srrentaladmin
  // models (fullname vs name, image vs profile_image) and adjust the
  // fallback chains below if needed.
  const getRaisedByInfo = (dispute) => {
    if (dispute.srcustomer) {
      const c = dispute.srcustomer;
      return {
        name:  c.fullname || c.name || c.full_name,
        email: c.email,
        image: c.image || c.profile_image || c.avatar,
        role:  "Customer"
      };
    }
    if (dispute.srrentaladmin) {
      const r = dispute.srrentaladmin;
      return {
        name:  r.fullname || r.name || r.full_name,
        email: r.email,
        image: r.image || r.profile_image || r.avatar,
        role:  "Rental Admin"
      };
    }
    return { name: "—", email: null, image: null, role: null };
  };

  // ── "Raised To" is always the currently logged-in mechanic ──
  // since viewMyDisputes() is scoped to srmechanicadmin_id = loggedInMechanic.id
  const getRaisedToInfo = () => {
    return {
      name:  loggedInMechanic?.fullname || loggedInMechanic?.name || loggedInMechanic?.full_name,
      email: loggedInMechanic?.email,
      image: loggedInMechanic?.image || loggedInMechanic?.profile_image || loggedInMechanic?.avatar,
      role:  ""
    };
  };

  // ── User info renderer (image + full name + email + role tag) ──
  // `image` is expected to be the relative path/filename returned by the API;
  // it gets prefixed with baseUrlForFile to build the actual <img> src.
  const renderUserInfo = ({ name, email, image, role }) => {
    const initial = name ? name.charAt(0).toUpperCase() : "?";
    const imageSrc = image ? `${baseUrlForFile}/${image}` : null;
    return (
      <div className="ds-user-info">
        {imageSrc ? (
          <img src={imageSrc} alt={name || "user"} className="ds-user-avatar" />
        ) : (
          <div className="ds-user-avatar ds-user-avatar-fallback">{initial}</div>
        )}
        <div className="ds-user-text">
          <span className="ds-user-name">{name || "—"}</span>
          {email && <span className="ds-user-email">{email}</span>}
          {role && <span className="ds-user-role">{role}</span>}
        </div>
      </div>
    );
  };

  // ================================================================
  // RETURN
  // ================================================================
  return (
    <MechanicAdminLayout>
      <div className="ds-page">

        {/* ── Header ── */}
        <div className="ds-page-header">
          <h2 className="ds-page-title">Disputes</h2>
          <p className="ds-page-sub">Manage disputes raised by farmers and rental admin</p>
        </div>

        {/* ════ Stat Cards ════ */}
        <div className="ds-stats-grid">
          {[
            { icon: <FiAlertTriangle />, value: total,       label: "Total Disputes" },
            { icon: <FiClock />,         value: open,        label: "Open"           },
            { icon: <FiTool />,          value: underReview, label: "Under Review"   },
            { icon: <FiCheckCircle />,   value: resolved,    label: "Resolved"       },
          ].map((s, i) => (
            <div className="ds-stat-card" key={i}>
              <div className="ds-stat-icon">{s.icon}</div>
              <h3 className="ds-stat-value">{s.value}</h3>
              <p className="ds-stat-label">{s.label}</p>
            </div>
          ))}
        </div>

        {/* ════ Table Card ════ */}
        <div className="ds-table-card">

          {/* Topbar */}
          <div className="ds-table-topbar">
            <div className="ds-table-title-wrap">
              <h3 className="ds-table-title">All Disputes</h3>
              <span className="ds-table-count">{disputes.length} disputes</span>
            </div>
          </div>

          {/* Table */}
          <div className="ds-table-wrap">
            <table className="ds-table">
              <colgroup>
                <col /><col /><col /><col /><col /><col /><col />
              </colgroup>
              <thead>
                <tr>
                  <th>RAISED BY</th>
                  <th>RAISED TO</th>
                  <th>CATEGORY</th>
                  <th>STATUS</th>
                  <th>DATE</th>
                  <th>FORWARDED</th>
                  <th>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {disputes.length > 0 ? (
                  disputes.map((dispute) => {
                    const isForwarded = !!dispute.forwarded;
                    return (
                      <tr key={dispute.id}>
                        <td>{renderUserInfo(getRaisedByInfo(dispute))}</td>
                        <td>{renderUserInfo(getRaisedToInfo())}</td>
                        <td><span className="ds-reason-tag">{dispute.category}</span></td>
                        <td>{getStatusBadge(dispute.status)}</td>
                        <td className="ds-td-date">{new Date(dispute.date).toLocaleDateString()}</td>
                        <td>{getForwardedBadge(isForwarded)}</td>
                        <td>
                          <div className="ds-action-menu">
                            <button
                              className="ds-dots-btn"
                              onClick={() => setActiveMenu(activeMenu === dispute.id ? null : dispute.id)}
                            >
                              <FiMoreVertical size={18} />
                            </button>
                            {activeMenu === dispute.id && (
                              <div className="ds-dropdown">
                                <button
                                  className="ds-dropdown-item ds-item-view"
                                  onClick={() => openDispute(dispute)}
                                >
                                  <FiEye size={14} /> Resolve Dispute
                                </button>
                                {!isForwarded && (
                                  <button
                                    className="ds-dropdown-item ds-item-view"
                                    disabled={forwarding}
                                    onClick={() => handleForwardToSuperadmin(dispute)}
                                  >
                                    <FiSend size={14} /> Forward to Superadmin
                                  </button>
                                )}
                                <button
                                  className="ds-dropdown-item ds-item-delete"
                                  onClick={() => deleteDispute(dispute.id)}
                                >
                                  <FiTrash2 size={14} /> Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="ds-empty-row">
                      <FiAlertTriangle size={32} color="#FDB714" />
                      <p>No disputes found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════
          RESOLVE DISPUTE MODAL
      ════════════════════════════════ */}
      {showModal && selectedDispute && (
        <>
          <div className="ds-backdrop" onClick={closeModal} />
          <div className="ds-modal">
            <div className="ds-modal-content ds-modal-lg">

              {/* Dark Header */}
              <div className="ds-modal-dark-header">
                <div>
                  <p className="ds-modal-dark-title">Resolve Dispute</p>
                  <p className="ds-modal-dark-sub">Full breakdown of dispute #{selectedDispute.id}</p>
                </div>
                <button className="ds-modal-dark-close" onClick={closeModal}>
                  <FiX />
                </button>
              </div>

              <div className="ds-modal-body">

                {/* Status + Date row */}
                <div className="ds-view-id-row">
                  {getStatusBadge(selectedDispute.status)}
                  {selectedDispute.forwarded && (
                    <span className="ds-badge ds-badge-resolved">Forwarded to Superadmin</span>
                  )}
                  <span className="ds-view-date">
                    {new Date(selectedDispute.date).toLocaleDateString()}
                  </span>
                </div>

                {/* Dispute Info */}
                <p className="ds-section-title">Dispute Information</p>
                <div className="ds-detail-grid">
                  <div className="ds-detail-item">
                    <span className="ds-detail-label">Raised By</span>
                    {renderUserInfo(getRaisedByInfo(selectedDispute))}
                  </div>
                  <div className="ds-detail-item">
                    <span className="ds-detail-label">Raised To</span>
                    {renderUserInfo(getRaisedToInfo())}
                  </div>
                  <div className="ds-detail-item">
                    <span className="ds-detail-label">Category</span>
                    <span className="ds-detail-value">{selectedDispute.category || "—"}</span>
                  </div>
                  <div className="ds-detail-item">
                    <span className="ds-detail-label">Date Filed</span>
                    <span className="ds-detail-value">{new Date(selectedDispute.date).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Description */}
                {selectedDispute.description && (
                  <div className="ds-description-box" style={{ marginBottom: "18px" }}>
                    <p className="ds-desc-label">Description</p>
                    <p className="ds-desc-text">{selectedDispute.description}</p>
                  </div>
                )}

                {/* Chat */}
                <p className="ds-section-title">Dispute Resolution Chat</p>
                <div className="ds-chat-box">
                  <div className="ds-chat-messages">
                    {chatMessages.length > 0 ? (
                      chatMessages.map((msg) => {
                        const isMe = msg.sender === "mechanic";
                        return (
                          <div
                            key={msg.id}
                            className={`ds-chat-row ${isMe ? "ds-chat-mine" : "ds-chat-other"}`}
                          >
                            <div className={`ds-chat-bubble ${isMe ? "ds-bubble-mine" : "ds-bubble-other"}`}>
                              <span className="ds-chat-sender">{getSenderLabel(msg.sender)}</span>
                              <p className="ds-chat-text">{msg.message}</p>
                              <span className="ds-chat-time">{formatTime(msg.created_at)}</span>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div style={{ textAlign: "center", color: "#999", padding: "30px 0" }}>
                        No messages yet. Start the conversation!
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="ds-chat-input-row">
                    <input
                      type="text"
                      className="ds-chat-input"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") sendMessage();
                      }}
                      placeholder="Type your message..."
                    />
                    <button
                      className="ds-chat-send-btn"
                      onClick={sendMessage}
                      disabled={!message.trim() || !conversationId}
                    >
                      <FiSend size={14} /> Send
                    </button>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="ds-modal-footer">
                <button
                  className="ds-modal-btn ds-btn-resolve-footer"
                  onClick={markAsResolved}
                  disabled={selectedDispute.status === "resolved"}
                  style={{ opacity: selectedDispute.status === "resolved" ? 0.5 : 1 }}
                >
                  <FiCheckCircle size={14} style={{ marginRight: 6 }} />
                  Mark as Resolved
                </button>
                <button className="ds-modal-btn ds-btn-close-modal" onClick={closeModal}>
                  Cancel
                </button>
              </div>

            </div>
          </div>
        </>
      )}

    </MechanicAdminLayout>
  );
}